// HubLayout — Shared layout for all 9 hub pages
// iOS HIG: sticky nav with back, hero card with stats, sectioned sub-feature cards

import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

export interface HubSection {
  title: string;
  items: { to: string; icon: string; iconBg: string; label: string; sub: string; badge?: { text: string; color: string; bg: string } }[];
}

export interface HubHeroStat {
  label: string;
  value: string;
  color?: string;
  sub?: string;
}

export interface HubHero {
  title: string;
  subtitle: string;
  gradient: string;
  emoji: string;
  stats?: HubHeroStat[];
}

export function HubLayout({
  title,
  hero,
  sections,
  quickActions,
  footerNote,
  testIdRoot,
}: {
  title: string;
  hero: HubHero;
  sections: HubSection[];
  quickActions?: { to: string; label: string; icon: string; testId: string }[];
  footerNote?: string;
  testIdRoot: string;
}) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav('/portal/more')} data-testid={`${testIdRoot}-back`} className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold" data-testid={`${testIdRoot}-title`}>{title}</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: hero.gradient, color: 'white' }} data-testid={`${testIdRoot}-hero`}>
          <div className="text-[36px] mb-1">{hero.emoji}</div>
          <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.8)' }}>{title}</div>
          <h1 className="mt-1 text-[22px] font-bold tracking-tight">{hero.title}</h1>
          <p className="text-[12px] mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>{hero.subtitle}</p>
          {hero.stats && (
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              {hero.stats.map((s) => (
                <div key={s.label} className="rounded-[12px] bg-white/15 backdrop-blur-md p-2">
                  <div className="text-[16px] font-bold tabular-nums" style={{ color: s.color || 'white' }}>{s.value}</div>
                  <div className="text-[9px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.75)' }}>{s.label}</div>
                  {s.sub && <div className="text-[8px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{s.sub}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      {quickActions && quickActions.length > 0 && (
        <div className="mx-4 mt-3 grid grid-cols-3 gap-2" data-testid={`${testIdRoot}-quick`}>
          {quickActions.map((q) => (
            <button
              key={q.to}
              onClick={() => nav(q.to)}
              data-testid={q.testId}
              className="rounded-[12px] border border-tf-border bg-white p-2.5 text-center active:scale-[0.97] transition"
            >
              <div className="text-[22px]">{q.icon}</div>
              <div className="text-[10px] font-semibold text-tf-text-1 mt-0.5 leading-tight">{q.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Sections */}
      {sections.map((s, i) => (
        <div key={s.title + i} data-testid={`${testIdRoot}-section-${i}`}>
          <h2 className="mt-5 px-4 pb-2 text-[11px] font-semibold uppercase tracking-wider text-tf-text-3">{s.title}</h2>
          <div className="mx-4 ios26-list bg-white">
            {s.items.map((item, j, a) => (
              <button
                key={item.to}
                onClick={() => nav(item.to)}
                data-testid={`${testIdRoot}-${item.to.replace(/\//g, '-')}`}
                className={['flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tf-surface-1 transition', j < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[18px]" style={{ background: item.iconBg }}>{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-semibold text-tf-text-1">{item.label}</span>
                    {item.badge && (
                      <span className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase" style={{ backgroundColor: item.badge.bg, color: item.badge.color }}>{item.badge.text}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-tf-text-3 mt-0.5">{item.sub}</div>
                </div>
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted shrink-0" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            ))}
          </div>
        </div>
      ))}

      {footerNote && (
        <p className="mt-4 mx-4 text-center text-[10px] text-tf-text-3">{footerNote}</p>
      )}
    </div>
  );
}
